import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Fingerprint, Star, Triangle, Circle, Layers, Zap, Users, ChevronRight } from "lucide-react";

export const ProcessSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const days = [
    {
      day: 1,
      title: "Verrou ADN",
      description: "Ton vocabulaire signature",
      icon: Fingerprint,
    },
    {
      day: 2,
      title: "NOVA",
      description: "10 hooks + 3 contenus prêts",
      icon: Star,
    },
    {
      day: 3,
      title: "PRISME",
      description: "Page courte + \"ce que ce n'est pas\"",
      icon: Triangle,
    },
    {
      day: 4,
      title: "PORTAIL",
      description: "Tunnel stories 12 écrans",
      icon: Circle,
    },
    {
      day: 5,
      title: "NOYAU",
      description: "1 protocole client + journaling",
      icon: Layers,
    },
    {
      day: 6,
      title: "FRACTURE + TRAJECTOIRE",
      description: "Décision + plan 14 jours",
      icon: Zap,
    },
    {
      day: 7,
      title: "DOUBLE",
      description: "Consolidation + routine hebdo",
      icon: Users,
    },
  ];

  return (
    <section 
      ref={ref}
      className="section-padding bg-white relative overflow-hidden"
      data-testid="process-section"
    >
      <div className="lumen-container">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="font-cinzel text-sm tracking-[0.3em] text-gold-metallic uppercase mb-4 block">
              Ultra simple
            </span>
            <h2 className="section-title font-cormorant text-text-main mb-4">
              Comment ça se passe
            </h2>
            <p className="font-manrope text-text-muted max-w-xl mx-auto">
              Tu n'as pas besoin de 12 heures de vidéos. Tu as besoin d'un mode d'emploi qui ne te donne pas envie de fuir.
            </p>
          </motion.div>

          {/* Simple steps */}
          <motion.div
            className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {["Tu copies-colles les prompts", "Tu poses ton Verrou ADN", "Tu exécutes le Défi 7 jours"].map((step, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center font-cinzel font-semibold text-emerald-700">
                  {index + 1}
                </span>
                <span className="font-manrope text-text-main">{step}</span>
                {index < 2 && <ChevronRight className="w-5 h-5 text-emerald-300 hidden md:block" />}
              </div>
            ))}
          </motion.div>

          {/* Challenge title */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <span className="inline-block px-6 py-2 bg-gradient-to-r from-emerald-100 to-amber-100 rounded-full">
              <span className="font-cormorant text-xl text-text-main">
                Défi 7 jours <span className="text-text-muted">(proposé)</span>
              </span>
            </span>
          </motion.div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line for mobile, horizontal for desktop */}
            <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-200 via-amber-200 to-purple-200" />
            
            <div className="grid grid-cols-1 md:grid-cols-7 gap-6 md:gap-4">
              {days.map((item, index) => (
                <motion.div
                  key={item.day}
                  className="relative"
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                >
                  {/* Day number */}
                  <div className="flex md:justify-center mb-4">
                    <div className="timeline-dot bg-white border-2 border-emerald-400">
                      <span className="text-sm">J{item.day}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="md:text-center pl-16 md:pl-0">
                    <div className="flex md:justify-center mb-2">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                        <item.icon className="w-4 h-4 text-emerald-600" />
                      </div>
                    </div>
                    <h4 className="font-manrope font-semibold text-sm text-text-main mb-1">
                      {item.title}
                    </h4>
                    <p className="font-manrope text-xs text-text-muted">
                      {item.description}
                    </p>
                  </div>

                  {/* Mobile timeline line */}
                  <div className="md:hidden absolute left-6 top-12 bottom-0 w-0.5 bg-gradient-to-b from-emerald-200 to-transparent" />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Bottom message */}
          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <p className="font-cormorant text-xl md:text-2xl text-text-main">
              Objectif : en une semaine tu ne "comprends" pas…
              <br />
              <span className="text-emerald-700 font-medium italic">tu installes.</span>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
